import { useState } from 'react';
import api from 'api';
import { useDispatch, useReduxState } from 'hooks';
import {
  attributesConditionsMatch,
  canUserCustomAttributeBeEdited,
  canUserCustomAttributeBeViewed,
  getApplicableUserCustomAttributes
} from 'shared-helpers/customAttributes';
import {
  decodeCustomAttributeKeyForForm,
  encodeCustomAttributeKeyForForm,
  shouldShowUserCustomAttributesForm
} from 'lib/customAttributes';
import { shouldShowUserNameForm } from 'lib/users';
import { Button } from 'components/common';
import { Form } from 'components/common/form';
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalPortal
} from 'components/common/modal';
import { UserCustomAttribute } from 'components/user';

import type { FormState } from 'informed';
import type {
  UserCustomAttribute as IUserCustomAttribute,
  UserCustomAttribute as UserCustomAttributeType,
  UserCustomAttributes
} from 'shared-types';

interface Props {
  guestInfoModalVisible: boolean;
}

const UserCustomAttributesForm = ({ guestInfoModalVisible }: Props) => {
  const { dispatch } = useDispatch();

  const { language, messages, serverConfig, user } = useReduxState();

  const { userCustomAttributes, userCustomAttributesForm } = serverConfig;

  const [isSubmitting, setIsSubmitting] = useState(false);

  const formIsValid = (
    formState: FormState<UserCustomAttributes>,
    missingAttributes: IUserCustomAttribute[]
  ) => {
    if (!missingAttributes.length) return true;
    if (formState.invalid) return false;

    const decodedCustomAttributes = Object.fromEntries(
      Object.entries(formState.values || {}).map(([key, value]) => [
        decodeCustomAttributeKeyForForm(key),
        value
      ])
    );

    return !getMissingAttributes(getApplicableAttributes(), {
      ...(user?.customAttributes || {}),
      ...decodedCustomAttributes
    }).find(
      (attribute) =>
        !formState.values[encodeCustomAttributeKeyForForm(attribute.key)]
    );
  };

  const getApplicableAttributes = () => {
    return getApplicableUserCustomAttributes(user, userCustomAttributes).filter(
      (attribute) =>
        // include only enabled attributes
        attribute.userAttributesFormSettings?.isEnabled &&
        // hide attributes that have been pre-filled
        !(
          attribute.userAttributesFormSettings?.hideIfSet &&
          user?.customAttributes?.[attribute.key] !== undefined
        ) &&
        // hide attributes that are not viewable
        canUserCustomAttributeBeViewed({
          allowUserPermissionForAdmin: true,
          user,
          userCustomAttribute: attribute
        })
    );
  };

  const getMissingAttributes = (
    applicableAttributes: UserCustomAttributeType[],
    values: UserCustomAttributes
  ) => {
    return applicableAttributes.filter(
      (attribute) =>
        // the attribute applies to the user
        attributesConditionsMatch({
          conditions: attribute.conditions,
          user,
          customAttributeValues: values
        }) &&
        // user can edit the attribute
        canUserCustomAttributeBeEdited({
          allowEditingUnsetValueIfEnabled: true,
          allowUserPermissionForAdmin: true,
          user,
          userCustomAttribute: attribute
        }) &&
        // attribute is required
        attribute.isRequired?.value &&
        // the required conditions are met
        attributesConditionsMatch({
          conditions: attribute.isRequired?.conditions || [],
          customAttributeValues: values,
          isRequiredCheck: true,
          user
        }) &&
        // user has no value for that attribute
        (!values?.[attribute.key] ||
          (Array.isArray(values?.[attribute.key]) &&
            !(values[attribute.key] as string[]).length))
    );
  };

  const handleSubmit = async (values: UserCustomAttributes) => {
    try {
      setIsSubmitting(true);

      const { customAttributesRequiredPasswords, ...otherValues } = values;

      const decodedCustomAttributes = Object.fromEntries(
        Object.entries(otherValues || {}).map(([key, value]) => [
          decodeCustomAttributeKeyForForm(key),
          value
        ])
      );

      const response = await api.post(`/auth/profile`, {
        customAttributes: {
          ...(user.customAttributes || {}),
          ...decodedCustomAttributes
        },
        customAttributesRequiredPasswords
      });

      setIsSubmitting(false);

      if (response.data?.success) {
        dispatch.setUser({
          ...user,
          customAttributes: { ...decodedCustomAttributes }
        });
        dispatch.addNotice({
          text: messages.commonSavedSuccessfully,
          type: 'success'
        });
        window.location.reload();
      } else if (response.data?.error === 'invalid_current_password') {
        await dispatch.handleErrorDispatch(
          null,
          messages.authCurrentPasswordInvalid
        );
      } else {
        throw new Error('error');
      }
    } catch (err) {
      setIsSubmitting(false);
      await dispatch.handleErrorDispatch(err);
    }
  };

  // do not show for vuoadmin
  if (user?.id === 1 || user?.roles.includes('vuoadmin')) return null;

  // do not show if user name form is visible
  if (serverConfig.userUseName && shouldShowUserNameForm(user)) return null;

  // do not show if guest info is visible
  if (user?.roles.includes('guest') && guestInfoModalVisible) return null;

  // show only if form is enabled
  if (!shouldShowUserCustomAttributesForm(user, serverConfig)) return null;

  // do not show if there are no missing attributes
  const applicableUserCustomAttributes = getApplicableAttributes();

  const missingAttributes = getMissingAttributes(
    applicableUserCustomAttributes,
    user?.customAttributes
  );
  if (!missingAttributes?.length) return null;

  const initialValues = applicableUserCustomAttributes.reduce(
    (values, attribute) => {
      values[encodeCustomAttributeKeyForForm(attribute.key)] =
        user?.customAttributes?.[attribute.key] !== undefined
          ? user.customAttributes?.[attribute.key]
          : attribute.userAttributesFormSettings?.defaultValue;
      return values;
    },
    {}
  );

  return (
    <ModalPortal>
      <Form initialValues={initialValues} onSubmit={handleSubmit}>
        {({ formState }) => (
          <Modal
            title={messages.profileInformation}
            visible={true}
            width="wide"
          >
            <ModalContent>
              {userCustomAttributesForm.description ? (
                <div
                  dangerouslySetInnerHTML={{
                    __html:
                      userCustomAttributesForm.descriptionTranslations?.[
                        language
                      ] || userCustomAttributesForm.description
                  }}
                />
              ) : null}

              {applicableUserCustomAttributes.map((attribute, i) => {
                const customAttributes: UserCustomAttributes = {
                  ...user.customAttributes,
                  ...(Object.fromEntries(
                    Object.entries(formState.values || {}).map(
                      ([key, value]) => [
                        decodeCustomAttributeKeyForForm(key),
                        value
                      ]
                    )
                  ) as UserCustomAttributes)
                };

                const conditionsMatch = attributesConditionsMatch({
                  conditions: attribute.conditions,
                  customAttributeValues: customAttributes,
                  user
                });
                if (!conditionsMatch) return null;

                const isReadOnly = !canUserCustomAttributeBeEdited({
                  allowEditingUnsetValueIfEnabled: true,
                  allowUserPermissionForAdmin: true,
                  user,
                  userCustomAttribute: attribute
                });

                const isRequired = attribute.isRequired?.value
                  ? attributesConditionsMatch({
                      conditions: attribute.isRequired?.conditions || [],
                      customAttributeValues: customAttributes,
                      user
                    })
                  : false;

                const error =
                  formState.errors[
                    encodeCustomAttributeKeyForForm(attribute.key)
                  ];

                return (
                  <UserCustomAttribute
                    attribute={attribute}
                    calledFrom="userCustomAttributesForm"
                    customAttributeOriginalValues={user.customAttributes}
                    customAttributeValues={customAttributes}
                    error={error}
                    id={i}
                    isAttributeRequired={isRequired}
                    isReadOnly={isReadOnly}
                    key={`${attribute.key}-${i}-${isRequired}`}
                  />
                );
              })}
            </ModalContent>

            <ModalFooter>
              <Button
                category="success"
                disabled={!formIsValid(formState, missingAttributes)}
                loading={isSubmitting}
                text={messages.commonSave}
                type="submit"
              />
            </ModalFooter>
          </Modal>
        )}
      </Form>
    </ModalPortal>
  );
};

export default UserCustomAttributesForm;
